// =============================================================================
// track — anonymous journey instrumentation
// =============================================================================
// One rule governs this whole file: IT MUST NEVER AFFECT WHETHER SOMEONE CAN
// JOIN. Every call is wrapped, fire-and-forget, and returns void. If the
// network is down, if storage throws, if the browser has no sendBeacon — the
// user's journey is byte-for-byte what it would have been.
//
// That is not a nicety. On 2026-08-20 a guard placed IN FRONT of the login
// broke signup completely for 70 minutes. Instrumentation sits BESIDE the
// path, never on it.
//
// WHY sendBeacon AND NOT fetch: several of these fire immediately before the
// page navigates away (the Google tap is the obvious one). A fetch is cancelled
// when the document unloads; a beacon is handed to the browser to deliver
// afterwards. The tap event would be the one we lose, and it is the one we
// most need.
// =============================================================================

const SID_KEY = "__degel_sid"
const ENDPOINT = "/api/auth-event"

/** Milliseconds within which an identical event is treated as a duplicate. */
const DEDUPE_MS = 10_000

export type TrackEvent =
  | "welcome_view"
  | "video_play"
  | "continue_tap"
  | "login_view"
  | "clerk_ready"
  | "login_blocked_tap"
  | "google_tap"
  | "callback_seen"
  | "callback_stall"
  | "callback_retry"
  | "callback_exhausted"
  | "sid_split"
  | "signup_view"
  | "signup_ok"
  | "signup_fail"

/**
 * A random id for THIS browser session. Not an identity: it lives in
 * sessionStorage, dies with the tab, and links to nothing about the person.
 *
 * It exists because the alternative — grouping by ua_hash — is a DEVICE
 * fingerprint. Two people on the same phone model collide into one journey,
 * and one person returning tomorrow looks like a continuation of today. On
 * 21 Aug that ambiguity is what made a retry analysis unreadable.
 *
 * EXPORTED since 22 Aug because the OAuth hop is the one place sessionStorage
 * can vanish, which makes this id least reliable exactly where it is most
 * needed. The login page threads it through Clerk's redirectUrlComplete so the
 * far side can tell "a different person" from "the same person, new session".
 */
export function sid(): string {
  try {
    let v = sessionStorage.getItem(SID_KEY)
    if (!v) {
      v =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2) + Date.now().toString(36)
      sessionStorage.setItem(SID_KEY, v)
    }
    return v
  } catch {
    // Private mode can throw on storage. An unlinked journey still counts.
    return "nostore"
  }
}

/**
 * Suppresses an identical event fired twice in quick succession.
 *
 * Needed because 6 of 54 taps on 21 Aug were the same press recorded twice —
 * a double-tap, or a component mounting twice — and they made a retry look
 * like it had happened when it had not. A duplicate is not just noise here;
 * it manufactures a second attempt that never occurred.
 */
const lastSent = new Map<string, number>()

export function track(event: TrackEvent, detail?: string): void {
  try {
    const now = Date.now()
    const key = `${event}:${detail ?? ""}`
    const prev = lastSent.get(key)
    if (prev !== undefined && now - prev < DEDUPE_MS) return
    lastSent.set(key, now)

    navigator.sendBeacon?.(
      ENDPOINT,
      new Blob(
        [
          JSON.stringify({
            event,
            sid: sid(),
            host: window.location.hostname,
            detail: detail ? detail.slice(0, 200) : undefined,
          }),
        ],
        { type: "application/json" },
      ),
    )
  } catch {
    /* deliberately swallowed — telemetry must never touch the user's path */
  }
}
